import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useState, useEffect, useCallback, useRef } from "react";
import { useIconSize } from "../../../app/hooks";
import { BiSave } from "react-icons/bi";
import TranslatableText from "../../translations/TranslatableText";
import Spinner from "../../spinner/Spinner";
import Button from "../../button/Button";

const TOOLBAR_OPTIONS = [
	["bold", "italic"],
	["link", "blockquote", "code-block"],
	[{ list: "ordered" }, { list: "bullet" }],
	
];

const SPINNER_SIZE = 36;
const SPINNER_PADDING = 8;

interface NoteTextEditorProps {
	notes: string | null;
	isLoading: boolean;
	onSave: (newNote: string) => void;
	errorMessage: string;
	placeholder?: string;
}

const NoteTextEditor = ({
	notes,
	isLoading,
	onSave,
	errorMessage,
	placeholder = "Write a note..."
}: NoteTextEditorProps) => {
	const [note, setNote] = useState<string | null>(notes);
	const editorRef = useRef<ReactQuill>(null);
	const iconSize = useIconSize(20);

	// Sync internal state with external notes prop
	useEffect(() => {
		setNote(notes);
	}, [notes]);

	const handleNoteChange = useCallback((content: string) => {
		setNote(content);
	}, []);
	
	const baseNoteString = "<p><br></p>"
	const isSaveDisabled = !note?.trim() || note === baseNoteString || isLoading;
	const handleSave = useCallback(() => {
		if (note?.trim() && !isSaveDisabled) {
			onSave(note);
		}
	}, [note, onSave, isSaveDisabled]);

	// Handle keyboard shortcuts
	const handleKeyDown = (event: KeyboardEvent) => {
		if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
			event.preventDefault();
			handleSave();
		}
	};


	return (
		<div className="note-text-editor">
			<ReactQuill
				ref={editorRef}
				className="note-text-editor-container"
				modules={{ toolbar: TOOLBAR_OPTIONS }}
				placeholder={placeholder}
				onChange={handleNoteChange}
				onKeyDown={handleKeyDown}
				bounds=".App"
				value={note ?? ""}
				theme="snow"
				aria-label="Note editor"
			/>
			<div className="w-100 d-flex justify-content-between align-items-center pt-2">
				<div className="text-error fs-13 ps-1" role="alert" aria-live="polite">
					{errorMessage}
				</div>

				<Button
					className="standard-action-button"
					onClick={handleSave}
					disabled={isSaveDisabled}
					title="Ctrl+Enter to save"
				>
					{isLoading ? (
						<Spinner
							size={SPINNER_SIZE}
							padding={SPINNER_PADDING}
							isBlack
							aria-label="Saving note"
						/>
					) : (
						<BiSave size={iconSize} aria-hidden="true" />
					)}
					<TranslatableText
						className="fs-12 ps-1 ff-normal-cond"
						translationKey="module.save_note"
					/>
				</Button>
			</div>
		</div>
	);
};

export default NoteTextEditor;
